/**
 * {project}（契約）の画面のタブ「定期請求」（#268 1.5a、docs/98、Figma「定期請求（10/29）」① 285:53）。
 *   見出し：定期請求・状態のしるし（継続／停止／終了）・契約名と開始月｜修正（テンプレートの窓）・⋯（停止／再開／終了）・「{m}月分作成」
 *   テンプレートの行：件名のテンプレート・請求日（自動の下書きの作成時期）・間隔・期間・前回の引用・備考のテンプレート
 *   明細のテンプレート（？）：{lineName}・数量・単位・単価・税区分・金額（税抜）と合計
 *   請求履歴（次の自動作成の日）：月・請求書・状態・請求日・金額（税抜）・入金
 * テンプレートの窓（RecurringEditor）は 作成と修正で同じ。明細は行の中で打つ（並べ替えは ↑↓ のボタン＝ドラッグだけにしない）
 */
import {
  RECURRING_DAY_RULE_LABEL,
  RECURRING_INTERVALS,
  RECURRING_MONTH_STATE_LABEL,
  RECURRING_MSG,
  RECURRING_STATUS_LABEL,
  TAX_CATEGORIES,
  TAX_CATEGORY_LABEL,
  TAX_CATEGORY_SHORT,
  INVOICE_STATE_LABEL,
  recurringDayLabel,
  recurringIntervalLabel,
  recurringIssueDate,
  recurringMonthLabel,
  recurringPeriodLabel,
  recurringSubject,
  roundAmount,
  type GroupDetail,
  type GroupRecurring,
  type RecurringCreateResult,
  type RecurringDayRule,
  type RecurringLine,
  type RecurringPlan,
  type RecurringPlanSave,
  type RecurringStatus,
  type TaxCategory,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { RowMenu } from "../../components/RowMenu.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { ErrorText, Help, Switch, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, todayJst, yen } from "../../lib/num.js";

const STATE_CHIP: Record<RecurringStatus, string> = { active: "chip ai", paused: "chip", ended: "chip quiet" };
const mOf = (month: string) => Number(month.slice(5, 7));
/** 全角の数字・記号を半角に（数字の欄は全角で打っても半角に直す＝docs/10 §9.1） */
const half = (s: string) => s.replace(/[０-９．－，]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[,\s]/g, "");

/** 契約のタブの中身を読む（タブの数にも使う） */
export function useGroupRecurring(groupId: string, enabled: boolean) {
  return useQuery({ queryKey: ["group-recurring", groupId], queryFn: () => api<GroupRecurring>("GET", `/groups/${groupId}/recurring`), enabled });
}

export function RecurringTab({ g }: { g: GroupDetail }) {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useGroupRecurring(g.id, true);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast, node: toastNode } = useToast();
  const data = q.data;
  const plan = data?.plan ?? null;
  const set = (v: GroupRecurring) => {
    qc.setQueryData(["group-recurring", g.id], v);
    void qc.invalidateQueries({ queryKey: ["recurring"] });
  };
  const setState = async (path: "pause" | "resume" | "end") => {
    if (!plan) return;
    try {
      set(await api<GroupRecurring>("POST", `/recurring/${plan.id}/${path}`));
      toast(path === "pause" ? RECURRING_MSG.paused : path === "resume" ? RECURRING_MSG.resumed : RECURRING_MSG.ended, undefined, "success", "recurring-state");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const createNext = async () => {
    if (!plan || !data?.nextMonth || busy) return;
    setBusy(true);
    try {
      const r = await api<RecurringCreateResult>("POST", "/recurring/create-month", { month: data.nextMonth, planIds: [plan.id] });
      void qc.invalidateQueries({ queryKey: ["group-recurring", g.id] });
      void qc.invalidateQueries({ queryKey: ["recurring"] });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      if (r.failed[0]) toast(r.failed[0].message, undefined, "error");
      else if (r.created[0]) nav(`/invoices/${r.created[0].invoiceId}`, { state: { notice: RECURRING_MSG.createdOne } });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };

  if (q.isError) return <div role="tabpanel" id="gpanel-recurring" aria-labelledby="gtab-recurring" className="gpanel"><ErrorText>{messageOf(q.error)}</ErrorText></div>;
  if (!data) return <div role="tabpanel" id="gpanel-recurring" aria-labelledby="gtab-recurring" className="gpanel rc-tab" aria-busy="true"><p className="hint">読み込んでいます…</p></div>;
  return (
    <div role="tabpanel" id="gpanel-recurring" aria-labelledby="gtab-recurring" className="gpanel rc-tab" data-testid="group-recurring">
      {!plan ? (
        <div className="gempty">
          <p style={{ margin: "0 0 10px" }}>
            {RECURRING_MSG.noPlan}
            <Help testId="help-recurring-tab" label={RECURRING_MSG.title} text={t(RECURRING_MSG.help)} />
          </p>
          <button type="button" className="btn primary" onClick={() => setEdit(true)} data-testid="recurring-plan-new">
            {RECURRING_MSG.createPlan}
          </button>
        </div>
      ) : (
        <>
          <div className="rc-head">
            <b className="rc-title">{RECURRING_MSG.title}</b>
            <span className={STATE_CHIP[plan.state]} data-testid="recurring-plan-state">{RECURRING_STATUS_LABEL[plan.state]}</span>
            <span className="muted rc-sub">
              {g.name}　{recurringPeriodLabel(plan.startMonth, null).replace(/ 〜.*$/, " 〜")}
            </span>
            <span className="sp" />
            <button type="button" className="btn" onClick={() => setEdit(true)} data-testid="recurring-plan-edit">
              {RECURRING_MSG.edit}
            </button>
            <RowMenu
              label={RECURRING_MSG.more}
              testId="recurring-plan-more"
              triggerClass="btn"
              items={[
                ...(plan.state === "active" ? [{ label: RECURRING_MSG.pause, onSelect: () => void setState("pause"), testId: "recurring-pause" }] : [{ label: RECURRING_MSG.resume, onSelect: () => void setState("resume"), testId: "recurring-resume" }]),
                ...(plan.state !== "ended" ? [{ label: RECURRING_MSG.end, onSelect: () => void setState("end"), testId: "recurring-end" }] : []),
              ]}
            />
            {data.nextMonth ? (
              <button type="button" className="btn primary" disabled={busy} onClick={() => void createNext()} data-testid="recurring-create-next">
                {RECURRING_MSG.createMonth(mOf(data.nextMonth))}
              </button>
            ) : null}
          </div>
          <dl className="kv rc-kv" data-testid="recurring-plan">
            <dt>{RECURRING_MSG.rowSubject}</dt>
            <dd data-testid="recurring-subject">{plan.subjectTemplate}</dd>
            <dt>{RECURRING_MSG.rowDay}</dt>
            <dd data-testid="recurring-day">
              {data.auto.on ? RECURRING_MSG.dayAuto(recurringDayLabel(plan.dayRule, plan.dayOfMonth), data.auto.leadDays) : recurringDayLabel(plan.dayRule, plan.dayOfMonth)}
            </dd>
            <dt>{RECURRING_MSG.rowInterval}</dt>
            <dd>{recurringIntervalLabel(plan.intervalMonths)}</dd>
            <dt>{RECURRING_MSG.rowPeriod}</dt>
            <dd data-testid="recurring-period">{recurringPeriodLabel(plan.startMonth, plan.endMonth)}</dd>
            <dt>{RECURRING_MSG.rowCopyLast}</dt>
            <dd>{plan.copyLast ? RECURRING_MSG.copyLastOn : RECURRING_MSG.copyLastOff}</dd>
            <dt>{RECURRING_MSG.rowMemo}</dt>
            <dd className="memotext">{plan.memoTemplate || <span className="muted">—</span>}</dd>
          </dl>
          <h3 className="rc-h">
            {RECURRING_MSG.linesTitle}
            <Help testId="help-recurring-lines" label={RECURRING_MSG.linesTitle} text={t(RECURRING_MSG.linesHelp)} />
          </h3>
          {plan.lines.length ? (
            <div className="tbl-wrap rc-box">
              <table className="list rc-lines" data-testid="recurring-lines">
                <thead>
                  <tr>
                    <th>{L.lineName}</th>
                    <th className="amt">数量</th>
                    <th>単位</th>
                    <th className="amt">単価</th>
                    <th>税区分</th>
                    <th className="amt">金額（税抜）</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.lines.map((l, i) =>
                    l.kind === "detail" ? (
                      <tr key={i} data-testid="recurring-line">
                        <td>
                          {l.name}
                          {l.spec ? <div className="muted sm">{l.spec}</div> : null}
                        </td>
                        <td className="amt num">{l.quantity ?? ""}</td>
                        <td>{l.unit}</td>
                        <td className="amt num">{l.unitPrice == null ? "" : yen(l.unitPrice)}</td>
                        <td>{TAX_CATEGORY_SHORT[l.taxCategory]}</td>
                        <td className="amt num">{yen(plan.lineAmounts[i] ?? 0)}</td>
                      </tr>
                    ) : (
                      <tr key={i} className={l.kind === "group" ? "rc-group" : "rc-note"} data-testid="recurring-line">
                        <td colSpan={6}>{l.name}</td>
                      </tr>
                    ),
                  )}
                  <tr className="rc-total">
                    <td colSpan={4} />
                    <td>{RECURRING_MSG.linesTotal}</td>
                    <td className="amt num" data-testid="recurring-lines-total">
                      <b>{yen(plan.subtotal)}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted" data-testid="recurring-lines-empty">{plan.copyLast ? RECURRING_MSG.linesEmptyCopy : RECURRING_MSG.linesEmpty}</p>
          )}
          <h3 className="rc-h">
            {RECURRING_MSG.historyTitle}
            {data.nextMonth && data.nextAutoOn ? (
              <span className="muted rc-note-sm" data-testid="recurring-next-auto">
                {RECURRING_MSG.historyNext(mOf(data.nextMonth), mdSlash(data.nextAutoOn), data.auto.leadDays)}
              </span>
            ) : null}
          </h3>
          {data.history.length ? (
            <div className="tbl-wrap rc-box">
              <table className="list rc-hist" data-testid="recurring-history">
                <thead>
                  <tr>
                    <th>{RECURRING_MSG.colHistMonth}</th>
                    <th>{RECURRING_MSG.colHistInvoice}</th>
                    <th>{RECURRING_MSG.colHistState}</th>
                    <th>{RECURRING_MSG.colHistIssue}</th>
                    <th className="amt">{RECURRING_MSG.colHistAmount}</th>
                    <th>{RECURRING_MSG.colHistPay}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h) => (
                    <tr key={h.invoiceId} className="r" tabIndex={0} onClick={() => nav(`/invoices/${h.invoiceId}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/invoices/${h.invoiceId}`)} data-testid="recurring-history-row">
                      <td>{recurringMonthLabel(h.month)}</td>
                      <td>
                        <Link to={`/invoices/${h.invoiceId}`} onClick={(e) => e.stopPropagation()}>{h.documentNo ?? RECURRING_MSG.invoiceLink} ›</Link>
                      </td>
                      <td>
                        <span className={h.state === "draft" ? "chip quiet" : "chip ai"}>{RECURRING_MONTH_STATE_LABEL[h.state]}</span>
                      </td>
                      <td className="num">{mdSlash(h.issueDate)}</td>
                      <td className="amt num">{yen(h.subtotal)}</td>
                      <td>
                        {!h.pay ? (
                          <span className="muted">—</span>
                        ) : h.pay.state === "paid" ? (
                          `${INVOICE_STATE_LABEL.paid}${h.pay.paidOn ? `（${mdSlash(h.pay.paidOn)}）` : ""}`
                        ) : (
                          <span className={h.pay.state === "overdue" ? "shu" : undefined}>{INVOICE_STATE_LABEL[h.pay.state]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted" data-testid="recurring-history-empty">{RECURRING_MSG.historyEmpty}</p>
          )}
        </>
      )}
      {edit ? (
        <RecurringEditor
          groupId={g.id}
          customerName={g.client.name}
          plan={plan}
          onClose={() => setEdit(false)}
          onSaved={(v) => {
            set(v);
            setEdit(false);
            toast(RECURRING_MSG.saved, undefined, "success", "recurring-saved");
          }}
        />
      ) : null}
      {toastNode}
    </div>
  );
}

type EditLine = { kind: RecurringLine["kind"]; name: string; spec: string; quantity: string; unit: string; unitPrice: string; taxCategory: TaxCategory };
const toEdit = (l: RecurringLine): EditLine => ({
  kind: l.kind,
  name: l.name,
  spec: l.spec,
  quantity: l.quantity == null ? "" : String(l.quantity),
  unit: l.unit,
  unitPrice: l.unitPrice == null ? "" : String(l.unitPrice),
  taxCategory: l.taxCategory,
});
const blankLine = (kind: RecurringLine["kind"] = "detail"): EditLine => ({ kind, name: "", spec: "", quantity: kind === "detail" ? "1" : "", unit: kind === "detail" ? "式" : "", unitPrice: "", taxCategory: "standard" });
const numOr = (s: string): number | null => {
  const v = half(s);
  if (v === "" || v === "-") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const ym = (m: string) => m.split("-").map(Number) as [number, number];

/** 年と月を選ぶ（type="month" は Safari の PC にない＝選ぶ欄2つ）。allowNone なら「終了月なし」 */
function MonthPick({ id, label, value, onChange, allowNone, testId }: { id: string; label: string; value: string | null; onChange: (v: string | null) => void; allowNone?: boolean; testId: string }) {
  const thisYear = Number(todayJst().slice(0, 4));
  const [y, m] = value ? ym(value) : [0, 0];
  const years = Array.from(new Set([...(y ? [y] : []), ...Array.from({ length: 8 }, (_, i) => thisYear - 2 + i)])).sort((a, b) => a - b);
  return (
    <div className="fld">
      <span className="lbl" id={`${id}_l`}>{label}</span>
      <span className="row rc-month" role="group" aria-labelledby={`${id}_l`}>
        <select
          className="inp"
          aria-label={`${label}（年）`}
          value={y || ""}
          onChange={(e) => (e.target.value ? onChange(`${e.target.value}-${String(m || 1).padStart(2, "0")}`) : onChange(null))}
          data-testid={`${testId}-year`}
        >
          {allowNone ? <option value="">{RECURRING_MSG.noEnd}</option> : null}
          {years.map((x) => (
            <option key={x} value={x}>{x}年</option>
          ))}
        </select>
        {y ? (
          <select className="inp" aria-label={`${label}（月）`} value={m} onChange={(e) => onChange(`${y}-${String(e.target.value).padStart(2, "0")}`)} data-testid={`${testId}-month`}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((x) => (
              <option key={x} value={x}>{x}月</option>
            ))}
          </select>
        ) : null}
      </span>
    </div>
  );
}

/** テンプレートの窓（作成・修正）。決定で保存（作った下書きは変わらない＝次の月から） */
export function RecurringEditor({ groupId, customerName, plan, onClose, onSaved }: { groupId: string; customerName: string; plan: RecurringPlan | null; onClose: () => void; onSaved: (v: GroupRecurring) => void }) {
  const L = useLabels();
  const thisMonth = todayJst().slice(0, 7);
  const [subject, setSubject] = useState(plan?.subjectTemplate ?? "{year}年{month}月分 ご請求");
  const [dayRule, setDayRule] = useState<RecurringDayRule>(plan?.dayRule ?? "month_end");
  const [day, setDay] = useState<number>(plan?.dayOfMonth ?? 25);
  const [interval, setInterval_] = useState<number>(plan?.intervalMonths ?? 1);
  const [start, setStart] = useState(plan?.startMonth ?? thisMonth);
  const [end, setEnd] = useState<string | null>(plan?.endMonth ?? null);
  const [copyLast, setCopyLast] = useState(plan?.copyLast ?? true);
  const [memo, setMemo] = useState(plan?.memoTemplate ?? "");
  const [lines, setLines] = useState<EditLine[]>(plan ? plan.lines.map(toEdit) : [blankLine()]);
  const [error, setError] = useState<string>();
  const code = plan?.lineRounding ?? "round";
  const body = (): RecurringPlanSave => ({
    subjectTemplate: subject,
    dayRule,
    dayOfMonth: dayRule === "day" ? day : null,
    intervalMonths: interval as 1 | 2 | 3 | 6 | 12,
    startMonth: start,
    endMonth: end,
    copyLast,
    memoTemplate: memo,
    // 名前の無い行は送らない（打ちかけの空の行）
    lines: lines
      .filter((l) => l.name.trim())
      .map((l) => ({
        kind: l.kind,
        name: l.name.trim(),
        spec: l.kind === "detail" ? l.spec.trim() : "",
        quantity: l.kind === "detail" ? numOr(l.quantity) : null,
        unit: l.kind === "detail" ? l.unit.trim() : "",
        unitPrice: l.kind === "detail" ? (numOr(l.unitPrice) == null ? null : Math.round(numOr(l.unitPrice)!)) : null,
        taxCategory: l.taxCategory,
      })),
    ...(plan ? { version: plan.updatedAt } : {}),
  });
  const save = useMutation({
    mutationFn: () => (plan ? api<GroupRecurring>("PUT", `/recurring/${plan.id}`, body()) : api<GroupRecurring>("POST", `/groups/${groupId}/recurring`, body())),
    onSuccess: onSaved,
    onError: (e) => setError(messageOf(e)),
  });
  const close = () => !save.isPending && onClose();
  const setLine = (i: number, p: Partial<EditLine>) => setLines((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const move = (i: number, d: -1 | 1) =>
    setLines((xs) => {
      const j = i + d;
      if (j < 0 || j >= xs.length) return xs;
      const n = [...xs];
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
  const amountOf = (l: EditLine) => (l.kind === "detail" ? roundAmount((numOr(l.quantity) ?? 0) * (numOr(l.unitPrice) ?? 0), code) : 0);
  const total = lines.reduce((a, l) => a + (l.name.trim() ? amountOf(l) : 0), 0);
  const example = recurringSubject(subject, start, customerName);
  const issueEx = recurringIssueDate(dayRule, day, start);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg rc-dlg" role="dialog" aria-label={RECURRING_MSG.dialogTitle} data-testid="recurring-editor" onKeyDown={(e) => e.key === "Escape" && close()}>
        <div className="dh">{RECURRING_MSG.dialogTitle}</div>
        <div className="db rc-form">
          <div className="fld">
            <label className="lbl" htmlFor="rc_subject">{RECURRING_MSG.rowSubject}</label>
            <input id="rc_subject" className="inp" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid="recurring-subject-input" />
            <span className="hint">{RECURRING_MSG.subjectHint(L.customer)}</span>
            {subject.trim() ? <span className="hint" data-testid="recurring-subject-example">{RECURRING_MSG.subjectExample(example)}</span> : null}
          </div>
          <div className="row rc-row">
            <div className="fld">
              <label className="lbl" htmlFor="rc_day">{RECURRING_MSG.rowDay}</label>
              <span className="row rc-month">
                <select id="rc_day" className="inp" value={dayRule} onChange={(e) => setDayRule(e.target.value as RecurringDayRule)} data-testid="recurring-day-rule">
                  {(["month_end", "month_start", "day"] as const).map((k) => (
                    <option key={k} value={k}>{RECURRING_DAY_RULE_LABEL[k]}</option>
                  ))}
                </select>
                {dayRule === "day" ? (
                  <select className="inp" aria-label={RECURRING_MSG.dayOfMonth} value={day} onChange={(e) => setDay(Number(e.target.value))} data-testid="recurring-day-of-month">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}日</option>
                    ))}
                  </select>
                ) : null}
              </span>
              <span className="hint" data-testid="recurring-issue-example">{`${recurringMonthLabel(start)}の請求日 ${mdSlash(issueEx)}`}</span>
            </div>
            <div className="fld">
              <label className="lbl" htmlFor="rc_interval">{RECURRING_MSG.rowInterval}</label>
              <select id="rc_interval" className="inp" value={interval} onChange={(e) => setInterval_(Number(e.target.value))} data-testid="recurring-interval">
                {RECURRING_INTERVALS.map((n) => (
                  <option key={n} value={n}>{recurringIntervalLabel(n)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="row rc-row">
            <MonthPick id="rc_start" label={RECURRING_MSG.startMonth} value={start} onChange={(v) => v && setStart(v)} testId="recurring-start" />
            <MonthPick id="rc_end" label={RECURRING_MSG.endMonth} value={end} onChange={setEnd} allowNone testId="recurring-end-month" />
          </div>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <Switch checked={copyLast} onChange={setCopyLast} label={RECURRING_MSG.rowCopyLast} testId="recurring-copy-last" />
            <Help testId="help-recurring-copy" label={RECURRING_MSG.rowCopyLast} text={RECURRING_MSG.copyLastOn} />
          </div>
          <div className="fld">
            <label className="lbl" htmlFor="rc_memo">{RECURRING_MSG.rowMemo}</label>
            <textarea id="rc_memo" className="inp" rows={2} maxLength={2000} value={memo} onChange={(e) => setMemo(e.target.value)} data-testid="recurring-memo" />
          </div>
          <div className="rc-h">
            {RECURRING_MSG.linesTitle}
            <Help testId="help-recurring-lines-edit" label={RECURRING_MSG.linesTitle} text={t(RECURRING_MSG.linesHelp)} />
          </div>
          <div className="tbl-wrap rc-box">
            <table className="list rc-edit" data-testid="recurring-edit-lines">
              <thead>
                <tr>
                  <th className="rc-kind">種類</th>
                  <th className="rc-name">{L.lineName}</th>
                  <th className="rc-spec">{L.lineSpec}</th>
                  <th className="amt rc-qty">数量</th>
                  <th className="rc-unit">単位</th>
                  <th className="amt rc-price">単価</th>
                  <th className="rc-tax">税区分</th>
                  <th className="amt rc-amt">金額</th>
                  <th className="rc-ops" aria-label="並べ替えと削除" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} data-testid="recurring-edit-line">
                    <td className="rc-kind">
                      <select className="inp" aria-label={`${i + 1}行目の種類`} value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value as EditLine["kind"] })} data-testid="recurring-line-kind">
                        {(["detail", "group", "note"] as const).map((k) => (
                          <option key={k} value={k}>{RECURRING_MSG.kind[k]}</option>
                        ))}
                      </select>
                    </td>
                    <td colSpan={l.kind === "detail" ? 1 : 7}>
                      <input className="inp" aria-label={`${i + 1}行目の${L.lineName}`} value={l.name} maxLength={200} onChange={(e) => setLine(i, { name: e.target.value })} data-testid="recurring-line-name" />
                    </td>
                    {l.kind === "detail" ? (
                      <>
                        <td>
                          <input className="inp" aria-label={`${i + 1}行目の${L.lineSpec}`} value={l.spec} maxLength={200} onChange={(e) => setLine(i, { spec: e.target.value })} data-testid="recurring-line-spec" />
                        </td>
                        <td className="amt">
                          <input className="inp num" type="text" inputMode="decimal" aria-label={`${i + 1}行目の数量`} value={l.quantity} onChange={(e) => setLine(i, { quantity: half(e.target.value) })} data-testid="recurring-line-qty" />
                        </td>
                        <td>
                          <UnitInput value={l.unit} onChange={(v) => setLine(i, { unit: v })} label={`${i + 1}行目の単位`} testId="recurring-line-unit" />
                        </td>
                        <td className="amt">
                          <input className="inp num" type="text" inputMode="numeric" aria-label={`${i + 1}行目の単価`} value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: half(e.target.value) })} data-testid="recurring-line-price" />
                        </td>
                        <td>
                          <select className="inp" aria-label={`${i + 1}行目の税区分`} value={l.taxCategory} onChange={(e) => setLine(i, { taxCategory: e.target.value as TaxCategory })} data-testid="recurring-line-tax">
                            {TAX_CATEGORIES.map((c) => (
                              <option key={c} value={c}>{TAX_CATEGORY_LABEL[c]}</option>
                            ))}
                          </select>
                        </td>
                        <td className="amt num" data-testid="recurring-line-amount">{yen(amountOf(l))}</td>
                      </>
                    ) : null}
                    <td className="rc-ops">
                      <button type="button" className="btn text sm" aria-label={`${i + 1}行目を${RECURRING_MSG.moveUp}`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="btn text sm" aria-label={`${i + 1}行目を${RECURRING_MSG.moveDown}`} disabled={i === lines.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className="btn text sm shu" aria-label={`${i + 1}行目を削除`} onClick={() => setLines((xs) => xs.filter((_, j) => j !== i))} data-testid="recurring-line-remove">×</button>
                    </td>
                  </tr>
                ))}
                <tr className="rc-total">
                  <td colSpan={7}>
                    <button type="button" className="btn text" disabled={lines.length >= 100} onClick={() => setLines((xs) => [...xs, blankLine()])} data-testid="recurring-line-add">＋ {RECURRING_MSG.addLine}</button>
                    <button type="button" className="btn text" disabled={lines.length >= 100} onClick={() => setLines((xs) => [...xs, blankLine("group")])} data-testid="recurring-group-add">＋ {RECURRING_MSG.addGroup}</button>
                  </td>
                  <td className="amt num" data-testid="recurring-edit-total">{yen(total)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn" disabled={save.isPending} onClick={close} data-testid="recurring-editor-cancel">取消</button>
          <button type="button" className="btn primary" disabled={save.isPending} onClick={() => save.mutate()} data-testid="recurring-editor-save">決定</button>
        </div>
      </div>
    </div>
  );
}
